<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { computed } from 'vue'
import type { IAoeCart } from '../../types/CartTypes'
import { FormatCurrency } from '../../services/currencyFormatter'
import {
  GetCartProductSubtotal,
  GetCartTotalBuyingDiscountAmount,
  GetCartOtherDiscountAmount,
} from '../../services/CartTotalsService'

const props = defineProps<{
  cart: IAoeCart | null
}>()

// Derived from the cart items - see CartTotalsService for why these are not
// taken from the cart-level fields returned by the server.
const productSubtotal = computed(() => GetCartProductSubtotal(props.cart))
const buyingDiscountAmount = computed(() =>
  GetCartTotalBuyingDiscountAmount(props.cart),
)
const otherDiscountAmount = computed(() => GetCartOtherDiscountAmount(props.cart))

function formatCurrency(value : number) {
  // Use the centralized FormatCurrency function from currencyFormatter.ts
  // Pass the currencyId from the cart, or null if not available
  const currencyId = props.cart?.currencyId || null
  return FormatCurrency(value, currencyId)
}

</script>
<template>
  <div v-if="props.cart != null" class="aoe-order-summary">
    <div class="row mt-3 party-order-entry-section-header-row">
      <div class="col-12 party-order-entry-section-header-div order-summary-section-header">
        {{ t('partyOrderEntry.OrderSummarySectionHeader') }}
      </div>
    </div>
    <div class="row mt-3 order-summary-row">
      <div class="col-6 col-md-3 order-summary-label">
        {{ t('partyOrderEntry.OrderSummaryProductSubtotal') }}
      </div>
      <div class="col-6 col-md-3 order-summary-value">
        {{ formatCurrency(productSubtotal) }}
      </div>
    </div>
    <div v-if="buyingDiscountAmount > 0" class="row mt-3 order-summary-row">
      <div class="col-6 col-md-3 order-summary-label">
        {{ t('partyOrderEntry.BuyingDiscount') }} -
      </div>
      <div class="col-6 col-md-3 order-summary-value">
        {{ formatCurrency(buyingDiscountAmount) }}
      </div>
    </div>
    <div class="row mt-3 order-summary-row">
      <div class="col-6 col-md-3 order-summary-label">
        {{ t('partyOrderEntry.OrderSummaryDiscount') }} -
      </div>
      <div class="col-6 col-md-3 order-summary-value">
        {{ formatCurrency(otherDiscountAmount) }}
      </div>
    </div>
    <div class="row mt-3 order-summary-row">
      <div class="col-6 col-md-3 order-summary-label">
        {{ t('partyOrderEntry.OrderSummarySubtotal') }}
      </div>
      <div class="col-6 col-md-3 order-summary-value">
        {{ props.cart && props.cart.subtotal ? formatCurrency(props.cart.subtotal) : formatCurrency(0) }}
      </div>
    </div>
    <div class="row mt-3 order-summary-row">
      <div class="col-6 col-md-3 order-summary-label">
        {{ t('partyOrderEntry.OrderSummaryShipping') }} +
      </div>
      <div class="col-6 col-md-3 order-summary-value">
        {{ props.cart && props.cart.shippingCost ? formatCurrency(props.cart.shippingCost) : formatCurrency(0) }}
      </div>
    </div>
    <div class="row mt-3 order-summary-row" v-if="props.cart?.totalTaxAmount > 0">
      <div class="col-6 col-md-3 order-summary-label">
        {{ t('partyOrderEntry.OrderSummaryTax') }}
      </div>
      <div class="col-6 col-md-3 order-summary-value">
        {{ props.cart && props.cart.totalTaxAmount ? formatCurrency(props.cart.totalTaxAmount) : formatCurrency(0) }}
      </div>
    </div>
    <div class="row mt-3 order-summary-row" v-if="props.cart.donationTotal > 0">
      <div class="col-6 col-md-3 order-summary-label">
        {{ t('partyOrderEntry.OrderSummaryDonations') }}
      </div>
      <div class="col-6 col-md-3 order-summary-value">
        {{ props.cart && props.cart.donationTotal ? formatCurrency(props.cart.donationTotal) : formatCurrency(0) }}
      </div>
    </div>
    <div class="row mt-3 order-summary-row" v-if="props.cart.feeTotal > 0">
      <div class="col-6 col-md-3 order-summary-label">
        {{ t('partyOrderEntry.OrderSummaryFees') }}
      </div>
      <div class="col-6 col-md-3 order-summary-value">
        {{ props.cart && props.cart.feeTotal ? formatCurrency(props.cart.feeTotal) : formatCurrency(0) }}
      </div>
    </div>
    <div class="row mt-3 order-summary-row">
      <div class="col-6 col-md-3 order-summary-label">
        {{ t('partyOrderEntry.OrderSummaryTotal') }}
      </div>
      <div class="col-6 col-md-3 order-summary-value">
        {{ props.cart && props.cart.total ? formatCurrency(props.cart.total) : formatCurrency(0) }}
      </div>
    </div>
    <div class="row mt-3 order-summary-row">
      <div class="col-6 col-md-3 order-summary-label">
        {{ t('partyOrderEntry.OrderSummaryPaymentsMade') }}
      </div>
      <div class="col-6 col-md-3 order-summary-value">
        {{ props.cart && props.cart.paymentTotal ? formatCurrency(props.cart.paymentTotal) : formatCurrency(0) }}
      </div>
    </div>
    <div class="row mt-3 order-summary-row">
      <div class="col-6 col-md-3 order-summary-label">
        {{ t('partyOrderEntry.OrderSummaryBalanceDue') }}
      </div>
      <div class="col-6 col-md-3 order-summary-value">
        {{ props.cart && props.cart.balance ? formatCurrency(props.cart.balance) : formatCurrency(0) }}
      </div>
    </div>
  </div>
</template>

<style lang="scss">

</style>
